import { useState, useEffect, useContext, useMemo, useCallback } from "react"
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../bootstrap/Modal"
import { useMenuData } from "./hooks/useMenuData"
import Button from "../../../bootstrap/Button"
import Alert from "../../../bootstrap/Alert"
import Badge from "../../../bootstrap/Badge"
import Icon from "../../../icon/Icon"
import { DefaultContext } from "../../../../contexts/default"
import CategoryDB from "../../../../database/wrappers/category"
import ProductDB from "../../../../database/wrappers/product"
import CategoryComplementDB from "../../../../database/wrappers/categoryComplement"
import ComplementDB from "../../../../database/wrappers/complement"
import Menu99FoodDB from "../../../../database/wrappers/menu99Food"
import StoreProductDB from "../../../../database/wrappers/storeProduct"
import PreAlert from "../../../../helpers/utils/preAlert"
import api from "../../../../services/api"
import { orderBy } from "firebase/firestore"
import CategoriesConfigStep from "./steps/CategoriesConfigStep"
import ProductsConfigStep from "./steps/ProductsConfigStep"
import ModifiersConfigStep from "./steps/ModifiersConfigStep"
import PreviewStep from "./steps/PreviewStep"
import { ResultsContext } from "../../../../contexts/results"

const NineNineFoodWizard = ({ open, handleOpen, existingMenuData }) => {
    const { establishment, store, onShowAlert } = useContext(DefaultContext)
    const { integrationsData } = useContext(ResultsContext)

    const [currentStep, setCurrentStep] = useState(0)
    const [isLoading, setIsLoading] = useState(true)

    const [allCategories, setAllCategories] = useState([])
    const [allProducts, setAllProducts] = useState([])
    const [allComplementCategories, setAllComplementCategories] = useState([])
    const [allComplements, setAllComplements] = useState([])
    const [storeProductsDictionary, setStoreProductsDictionary] = useState({})

    const [selectedCategories, setSelectedCategories] = useState([])
    const [selectedProducts, setSelectedProducts] = useState([])
    const [selectedModifiers, setSelectedModifiers] = useState([])

    const [isSaving, setIsSaving] = useState(false)
    const [isCopying, setIsCopying] = useState(false)

    const { getProduct, getCategory, getProductCategory } = useMenuData(allProducts, allCategories)

    useEffect(() => {
        if (!open) {
            setCurrentStep(0)
            setSelectedCategories([])
            setSelectedProducts([])
            setSelectedModifiers([])
        }
    }, [open])

    useEffect(() => {
        if (open && establishment && store) loadAllData()
    }, [open, establishment, store])

    const loadAllData = async () => {
        if (!establishment || !store) return
        setIsLoading(true)

        try {
            const [categories, products, complementCategories, complements, storeProducts] = await Promise.all([
                new CategoryDB(establishment.id).getAll(orderBy('name', 'asc')),
                new ProductDB(establishment.id).getAll(orderBy('name', 'asc')),
                new CategoryComplementDB(establishment.id).getAll(orderBy('name', 'asc')),
                new ComplementDB(establishment.id).getAll(orderBy('name', 'asc')),
                new StoreProductDB(establishment.id, store.id).getAll()
            ])

            const storeProdDict = {}
            storeProducts.forEach(sp => storeProdDict[sp.id] = sp)
            setStoreProductsDictionary(storeProdDict)

            const activeProducts = products.filter(p => {
                const storeProduct = storeProdDict[p.id]
                return p.active && (!storeProduct || storeProduct.active !== false)
            })

            const activeProductIds = new Set(activeProducts.map(p => p.id))
            const categoriesWithActiveProducts = categories.filter(c => {
                if (!c.active) return false
                return c.products?.some(prodId => activeProductIds.has(prodId))
            })

            setAllCategories(categoriesWithActiveProducts)
            setAllProducts(activeProducts)
            setAllComplementCategories(complementCategories.filter(c => c.active))
            setAllComplements(complements.filter(c => c.active))

            if (existingMenuData?.categories) {
                const activeProductIds = new Set(activeProducts.map(p => p.id))
                const filteredItems = (existingMenuData.items || []).filter(item =>
                    activeProductIds.has(item.app_item_id)
                )

                const filteredCategories = (existingMenuData.categories || [])
                    .map(cat => ({
                        ...cat,
                        app_item_ids: cat.app_item_ids.filter(id => activeProductIds.has(id))
                    }))
                    .filter(cat => cat.app_item_ids.length > 0)

                setSelectedCategories(filteredCategories)
                setSelectedProducts(filteredItems)
                setSelectedModifiers(existingMenuData.modifier_groups || [])
            }
        } catch (error) {
            console.error('Erro:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleCategoriesChange = useCallback((categories) => {
        setSelectedCategories(categories)
        const categoryIds = new Set(categories.map(c => c.app_category_id))
        setSelectedProducts(prev =>
            prev.filter(item => {
                const productCategoryId = getProductCategory(item.app_item_id)
                return productCategoryId && categoryIds.has(productCategoryId)
            })
        )
    }, [getProductCategory])

    const handleProductsChange = useCallback((products) => {
        setSelectedProducts(products)

        setSelectedCategories(prev => prev.map(cat => {
            const categoryProducts = products
                .filter(item => getProductCategory(item.app_item_id) === cat.app_category_id)
                .map(item => item.app_item_id)

            return {
                ...cat,
                app_item_ids: categoryProducts
            }
        }))

        const usedCategoryIds = new Set()
        products.forEach(item => {
            const product = getProduct(item.app_item_id)
            product?.complements?.forEach(id => usedCategoryIds.add(id))
        })

        setSelectedModifiers(prev => prev.filter(m => usedCategoryIds.has(m.app_modifier_group_id)))
    }, [getProduct, getProductCategory])

    const canGoNext = () => {
        if (currentStep === 0) return selectedCategories.length > 0
        if (currentStep === 1) return selectedProducts.length > 0
        return true
    }

    const handleNext = () => {
        if (currentStep < 3) setCurrentStep(prev => prev + 1)
    }

    const handleBack = () => {
        if (currentStep > 0) setCurrentStep(prev => prev - 1)
    }

    const handleClose = () => {
        setCurrentStep(0)
        setSelectedCategories([])
        setSelectedProducts([])
        setSelectedModifiers([])
        handleOpen(false)
    }

    const stepTitles = ["Categorias", "Produtos", "Complementos", "Preview"]

    const handleCategoryToggle = useCallback((category) => {
        setSelectedCategories(prev => {
            const isSelected = prev.some(c => c.app_category_id === category.id)
            if (isSelected) {
                const newCategories = prev.filter(c => c.app_category_id !== category.id)
                const categoryIds = new Set(newCategories.map(c => c.app_category_id))
                setSelectedProducts(items => items.filter(item => {
                    const productCategoryId = getProductCategory(item.app_item_id)
                    return productCategoryId && categoryIds.has(productCategoryId)
                }))
                return newCategories
            } else {
                return [...prev, {
                    app_category_id: category.id,
                    category_name: category.name,
                    app_item_ids: []
                }]
            }
        })
    }, [getProductCategory])

    const handleSelectAllCategories = useCallback(() => {
        handleCategoriesChange(allCategories.map(c => ({
            app_category_id: c.id,
            category_name: c.name,
            app_item_ids: []
        })))
    }, [allCategories, handleCategoriesChange])

    const handleClearCategories = useCallback(() => {
        handleCategoriesChange([])
    }, [handleCategoriesChange])

    const getCategoriesWithProducts = useMemo(() => {
        return selectedCategories
            .map(cat => {
                const originalCategory = getCategory(cat.app_category_id)
                const productIds = new Set(originalCategory?.products || [])
                return {
                    ...cat,
                    products: allProducts.filter(p => productIds.has(p.id))
                }
            })
            .filter(c => c.products.length > 0)
    }, [selectedCategories, getCategory, allProducts])

    const handleProductToggle = useCallback((product) => {
        setSelectedProducts(prev => {
            const isSelected = prev.some(sp => sp.app_item_id === product.id)
            if (isSelected) {
                const newProducts = prev.filter(sp => sp.app_item_id !== product.id)
                handleProductsChange(newProducts)
                return newProducts
            } else {
                const storeProduct = storeProductsDictionary[product.id]
                const price = (storeProduct?.price ?? product.price ?? 0)
                const newProducts = [...prev, {
                    app_item_id: product.id,
                    app_external_id: product.delivery_code,
                    item_name: product.name,
                    short_desc: product.description || "",
                    head_img: product.image_url || "",
                    price: price,
                    is_sold_separately: true,
                    status: 1,
                    app_modifier_group_ids: [],

                    ...(product.delivery_code && { app_external_id: product.delivery_code })
                }]
                handleProductsChange(newProducts)
                return newProducts
            }
        })
    }, [handleProductsChange, storeProductsDictionary])

    const handleSelectAllProducts = useCallback(() => {
        const all = getCategoriesWithProducts.flatMap(c =>
            c.products.map(p => {
                const storeProduct = storeProductsDictionary[p.id]
                const price = (storeProduct?.price ?? p.price ?? 0)
                return {
                    app_item_id: p.id,
                    app_external_id: p.delivery_code,
                    item_name: p.name,
                    short_desc: p.description || "",
                    head_img: p.image_url || "",
                    price: price,
                    is_sold_separately: true,
                    status: 1,
                    app_modifier_group_ids: [],

                    ...(p.delivery_code && { app_external_id: p.delivery_code })
                }
            })
        )
        handleProductsChange(all)
    }, [getCategoriesWithProducts, storeProductsDictionary])

    const handleClearProducts = useCallback(() => {
        handleProductsChange([])
    }, [handleProductsChange])

    const getAvailableModifierCategories = useMemo(() => {
        const usedCategoryIds = new Set()
        selectedProducts.forEach(item => {
            const product = getProduct(item.app_item_id)
            product?.complements?.forEach(id => usedCategoryIds.add(id))
        })

        return allComplementCategories
            .filter(cat => usedCategoryIds.has(cat.id))
            .map(cat => {
                const complementIds = new Set(cat.complements || [])
                return {
                    ...cat,
                    complements: allComplements.filter(c => complementIds.has(c.id))
                }
            })
            .filter(cat => cat.complements.length > 0)
    }, [selectedProducts, getProduct, allComplementCategories, allComplements])

    const handleModifierToggle = useCallback((category) => {
        setSelectedModifiers(prev => {
            const isSelected = prev.some(m => m.app_modifier_group_id === category.id)
            if (isSelected) {
                return prev.filter(m => m.app_modifier_group_id !== category.id)
            } else {
                return [...prev, {
                    app_modifier_group_id: category.id,
                    modifier_group_name: category.name,
                    is_required: category.min_item > 0 ? 1 : 2,
                    quantity_min_permitted: category.min_item || 0,
                    quantity_max_permitted: category.max_item || 1,
                    buy_mode: category.max_item > 1 ? 1 : 0,
                    app_mg_items: category.complements.map(c => ({
                        app_item_id: c.id,
                        item_name: c.name,
                        short_desc: c.description || "",
                        head_img: c.image_url || "",
                        price: c.price ?? 0,
                        status: 1,
                        ...(c.delivery_code && { app_external_id: c.delivery_code })
                    }))
                }]
            }
        })
    }, [])

    const handleSelectAllModifiers = useCallback(() => {
        setSelectedModifiers(getAvailableModifierCategories.map(c => ({
            app_modifier_group_id: c.id,
            modifier_group_name: c.name,
            is_required: c.min_item > 0 ? 1 : 2,
            buy_mode: c.max_item > 1 ? 1 : 0,
            quantity_min_permitted: c.min_item || 0,
            quantity_max_permitted: c.max_item || 1,
            app_mg_items: c.complements.map(comp => ({
                app_item_id: comp.id,
                item_name: comp.name,
                short_desc: comp.description || "",
                head_img: comp.image_url || "",
                price: comp.price ?? 0,
                status: 1,
                ...(comp.delivery_code && { app_external_id: comp.delivery_code })
            }))
        })))
    }, [getAvailableModifierCategories])

    const handleClearModifiers = useCallback(() => {
        setSelectedModifiers([])
    }, [])

    const handleSendTo99Food = async () => {
        setIsSaving(true)
        try {
            const validCategories = selectedCategories.filter(cat => cat.app_item_ids && cat.app_item_ids.length > 0)
            const validModifierGroups = selectedModifiers.filter(mod => mod.app_mg_items && mod.app_mg_items.length > 0)

            const selectedModifierIds = new Set(validModifierGroups.map(m => m.app_modifier_group_id))
            const itemsWithSelectedMG = selectedProducts.map(item => {
                const product = getProduct(item.app_item_id)
                const productMGs = new Set(product?.complements || [])
                const appliedMGs = Array.from(productMGs).filter(id => selectedModifierIds.has(id))
                return {
                    ...item,
                    app_modifier_group_ids: appliedMGs
                }
            })

            const finalJsonData = {
                menus: [{ app_menu_id: "1", menu_name: "Cardapio Principal" }],
                categories: validCategories,
                items: itemsWithSelectedMG,
                ...(validModifierGroups.length && { modifier_groups: validModifierGroups }),
                _status: "pending"
            }
            const menu99 = new Menu99FoodDB(establishment.id, store.id)
            await menu99.define("99Send", finalJsonData)

            await api.put(`routes99/store/${integrationsData.store_99}/menu`)
            onShowAlert(PreAlert.success("Cardápio salvo com sucesso!"))
            handleClose()
        } catch (err) {
            console.error("Erro ao salvar cardápio:", err)
            onShowAlert(PreAlert.error(err.response?.data?.message || "Erro ao salvar cardápio."))
        } finally {
            setIsSaving(false)
        }
    }

    const handleCopyToClipboard = async () => {
        setIsCopying(true);
        try {
            const validCategories = selectedCategories.filter(cat => cat.app_item_ids && cat.app_item_ids.length > 0)
            const validModifierGroups = selectedModifiers.filter(mod => mod.app_mg_items && mod.app_mg_items.length > 0)

            const finalJsonData = {
                menus: [{ app_menu_id: "1", menu_name: "Cardapio Principal" }],
                categories: validCategories,
                items: selectedProducts,
                ...(validModifierGroups.length && { modifier_groups: validModifierGroups })
            };

            await navigator.clipboard.writeText(JSON.stringify(finalJsonData, null, 2));

            onShowAlert(PreAlert.success("JSON copiado para a área de transferência!"));
        } catch (err) {
            console.error('Falha ao copiar:', err);
            onShowAlert(PreAlert.error("Falha ao copiar o JSON."));
        } finally {
            setTimeout(() => setIsCopying(false), 2000);
        }
    };


    const renderCurrentStep = () => {
        if (isLoading) {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-dark" />
                    <p className="mt-3">Carregando...</p>
                </div>
            )
        }

        if (currentStep === 0) {
            return (
                <CategoriesConfigStep
                    allCategories={allCategories}
                    allProducts={allProducts}
                    selectedCategories={selectedCategories}
                    onCategoryToggle={handleCategoryToggle}
                    onSelectAll={handleSelectAllCategories}
                    onClear={handleClearCategories}
                />
            )
        }

        if (currentStep === 1) {
            return (
                <ProductsConfigStep
                    categoriesWithProducts={getCategoriesWithProducts}
                    selectedProducts={selectedProducts}
                    onProductToggle={handleProductToggle}
                    onSelectAll={handleSelectAllProducts}
                    onClear={handleClearProducts}
                />
            )
        }

        if (currentStep === 2) {
            return (
                <ModifiersConfigStep
                    availableCategories={getAvailableModifierCategories}
                    selectedModifiers={selectedModifiers}
                    onModifierToggle={handleModifierToggle}
                    onSelectAll={handleSelectAllModifiers}
                    onClear={handleClearModifiers}
                />
            )
        }

        if (currentStep === 3) {
            return (
                <PreviewStep
                    data={{
                        menu: { app_menu_id: "1", menu_name: "Cardapio Principal" },
                        categories: selectedCategories,
                        items: selectedProducts,
                        modifier_groups: selectedModifiers
                    }}
                    allProducts={allProducts}
                    allComplements={allComplements}
                />
            )
        }

        return null
    }

    return (
        <Modal isOpen={open} setIsOpen={handleOpen} size='xl' isStaticBackdrop isAnimation={false}>
            <ModalHeader setIsOpen={handleOpen} className="bg-dark text-white">
                <ModalTitle>
                    99Food - Configuração de Menu
                    <small className="ms-2 text-white-50">
                        ({selectedCategories.length} categorias, {selectedProducts.length} produtos, {selectedModifiers.length} modificadores)
                    </small>
                </ModalTitle>
            </ModalHeader>
            <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="mb-4">
                    <div className="progress mb-3" style={{ height: '8px' }}>
                        <div className="progress-bar bg-dark" style={{ width: `${((currentStep + 1) / 4) * 100}%` }} />
                    </div>
                    <div className="d-flex justify-content-between">
                        {stepTitles.map((title, i) => (
                            <div key={i} className="text-center">
                                <Badge color={i <= currentStep ? 'primary' : 'dark'} className="mb-1">
                                    {i < currentStep ? '✓' : i + 1}
                                </Badge>
                                <div>
                                    <small className={i <= currentStep ? 'text-dark fw-bold' : 'text-muted'}>{title}</small>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <Alert color="dark" isLight className="mb-4">
                    <strong>Passo {currentStep + 1} de 4:</strong> {stepTitles[currentStep]}
                </Alert>

                {renderCurrentStep()}
            </ModalBody>
            <ModalFooter>
                <div className="d-flex justify-content-between w-100">
                    <div>
                        {currentStep > 0 && <Button color='dark' isLight onClick={handleBack}>Voltar</Button>}
                    </div>
                    <div className="d-flex gap-2">
                        {currentStep < 3 ? (
                            <Button color='dark' onClick={handleNext} disabled={!canGoNext()}>Próximo</Button>
                        ) : (
                            <>
                                <Button
                                    color="dark"
                                    icon={isCopying ? "CheckCircle" : "Copy"}
                                    onClick={handleCopyToClipboard}
                                    isLight
                                    disabled={isCopying}
                                >
                                    {isCopying ? 'Copiado!' : 'Copiar JSON'}
                                </Button>
                                <Button
                                    color="dark"
                                    icon="Send"
                                    onClick={handleSendTo99Food}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Salvando...' : 'Salvar Menu'}
                                    {isSaving && <Icon icon="Spinner" className="ms-2" color="white" spin />}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </ModalFooter>
        </Modal>
    )
}

export default NineNineFoodWizard